(() => {
  const instances = new WeakMap();

  const buildFormUrl = (form) => {
    const url = new URL(form.action, window.location.origin);
    url.search = '';
    new FormData(form).forEach((value, key) => {
      if (String(value) !== '') url.searchParams.append(key, String(value));
    });
    return url.href;
  };

  const fetchSection = async (section, url) => {
    const instance = instances.get(section);
    if (!instance) throw new Error('Product results instance missing');
    instance.controller?.abort();
    instance.controller = new AbortController();

    const requestUrl = new URL(url, window.location.origin);
    requestUrl.searchParams.set('section_id', section.dataset.sectionId);
    const response = await fetch(requestUrl, {
      headers: { 'X-Requested-With': 'XMLHttpRequest' },
      signal: instance.controller.signal
    });
    if (!response.ok) throw new Error('Product results request failed');
    const documentFragment = new DOMParser().parseFromString(await response.text(), 'text/html');
    const nextSection = documentFragment.querySelector('[data-product-results]');
    if (!nextSection) throw new Error('Product results section missing');
    return nextSection;
  };

  const announce = (section, message) => {
    const status = section.querySelector('[data-results-status]');
    if (!status) return;
    status.textContent = '';
    window.requestAnimationFrame(() => {
      if (status.isConnected) status.textContent = message || '';
    });
  };

  const renderSection = async (section, url, options = {}) => {
    const { pushState = true, focusResults = false } = options;
    const instance = instances.get(section);
    if (!instance) return;
    const requestId = ++instance.requestId;
    section.classList.add('is-loading');
    section.setAttribute('aria-busy', 'true');

    try {
      const nextSection = await fetchSection(section, url);
      section.innerHTML = nextSection.innerHTML;
      section.dispatchEvent(new CustomEvent('theme:content:updated', { bubbles: true }));
      announce(section, section.dataset.updatedText);
      if (pushState) window.history.pushState({}, '', url);
      if (focusResults) section.querySelector('[data-results-grid]')?.focus();
    } catch (error) {
      if (error.name !== 'AbortError') window.location.assign(url);
    } finally {
      if (instances.get(section)?.requestId === requestId) {
        section.classList.remove('is-loading');
        section.setAttribute('aria-busy', 'false');
      }
    }
  };

  const loadMore = async (section, link) => {
    const grid = section.querySelector('[data-results-grid]');
    const actions = section.querySelector('[data-results-actions]');
    if (!grid || !actions || !link.href) return;
    const idleLabel = link.dataset.idleLabel || link.textContent;
    link.dataset.idleLabel = idleLabel;
    link.setAttribute('aria-busy', 'true');
    link.textContent = section.dataset.loadingText || idleLabel;

    try {
      const nextSection = await fetchSection(section, link.href);
      const newItems = [...nextSection.querySelectorAll('[data-collection-product-item]')];
      const firstNewItem = newItems[0];
      newItems.forEach((item) => grid.appendChild(item));

      const nextLink = nextSection.querySelector('[data-load-more]');
      if (nextLink instanceof HTMLAnchorElement) {
        link.href = nextLink.href;
        link.textContent = idleLabel;
        link.removeAttribute('aria-busy');
      } else {
        actions.remove();
      }

      announce(section, section.dataset.updatedText);
      if (firstNewItem instanceof HTMLElement) {
        firstNewItem.tabIndex = -1;
        firstNewItem.focus();
      }
    } catch (error) {
      if (error.name === 'AbortError') return;
      link.textContent = section.dataset.retryText || idleLabel;
      link.removeAttribute('aria-busy');
      announce(section, section.dataset.retryText);
    }
  };

  const initProductResults = (scope = document) => {
    scope.querySelectorAll('[data-product-results]').forEach((section) => {
      if (instances.has(section)) return;
      const instance = { controller: null, filterTimer: null, requestId: 0 };
      instances.set(section, instance);

      section.addEventListener('submit', (event) => {
        const form = event.target;
        if (!(form instanceof HTMLFormElement) || !form.matches('[data-filters-form]')) return;
        event.preventDefault();
        renderSection(section, buildFormUrl(form));
      });

      section.addEventListener('change', (event) => {
        const target = event.target;
        if (!(target instanceof HTMLElement) || target.matches('input[name="q"]')) return;
        const form = target.closest('[data-filters-form]');
        if (!(form instanceof HTMLFormElement)) return;
        if (instance.filterTimer) window.clearTimeout(instance.filterTimer);
        instance.filterTimer = window.setTimeout(() => form.requestSubmit(), 150);
      });

      section.addEventListener('click', (event) => {
        const target = event.target instanceof Element ? event.target : null;
        const loadMoreLink = target?.closest('[data-load-more]');
        if (loadMoreLink instanceof HTMLAnchorElement) {
          event.preventDefault();
          loadMore(section, loadMoreLink);
          return;
        }

        const link = target?.closest('.product-results__pagination a, [data-results-ajax-link]');
        if (!(link instanceof HTMLAnchorElement)) return;
        event.preventDefault();
        renderSection(section, link.href, { focusResults: link.closest('.product-results__pagination') !== null });
      });
    });
  };

  const unloadProductResults = (scope) => {
    scope.querySelectorAll('[data-product-results]').forEach((section) => {
      const instance = instances.get(section);
      instance?.controller?.abort();
      if (instance?.filterTimer) window.clearTimeout(instance.filterTimer);
      instances.delete(section);
    });
  };

  window.addEventListener('popstate', () => {
    document.querySelectorAll('[data-product-results]').forEach((section) => {
      renderSection(section, window.location.href, { pushState: false });
    });
  });

  document.addEventListener('DOMContentLoaded', () => initProductResults());
  document.addEventListener('shopify:section:load', (event) => initProductResults(event.target));
  document.addEventListener('shopify:section:unload', (event) => unloadProductResults(event.target));
})();
